import { VOICE_TRANSCRIBE_MAX_SECONDS } from '@fos/shared/contracts/speech';
import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../lib/api';
import { SPEECH_MESSAGES } from './speech';
import { transcribeAudio } from './transcribe-client';

/**
 * Nagranie do transkrypcji na serwerze (Fala 10, silnik „OpenAI” albo zapas „Automatycznie”): MediaRecorder →
 * `POST /api/voice/transcribe` → tekst. Nagranie jest tylko w pamięci karty (fragmenty w tablicy), po wysłaniu
 * i przy anulowaniu tablica jest czyszczona, a mikrofon zwalniany (ścieżki zatrzymane) — nic nie jest zapisywane.
 * Limit długości: przeglądarka sama kończy nagranie po `maxSeconds` (domyślnie 2 min). Opcjonalnie koniec po ciszy
 * (analiza głośności przez Web Audio): po pierwszych słowach 1,6 s ciszy = koniec mówienia.
 */

export type RecorderStatus = 'idle' | 'recording' | 'transcribing' | 'denied' | 'error';

/**
 * Typy nagrania w kolejności preferencji (Chrome / Edge / Firefox: webm+opus, Safari: mp4) — tylko formaty przyjmowane
 * przez transkrypcję OpenAI (bez ogg; `VOICE_AUDIO_MIME_TYPES`).
 */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export function recorderSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as { MediaRecorder?: unknown }).MediaRecorder === 'function' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

function pickMime(): string | undefined {
  const MR = (window as unknown as { MediaRecorder?: { isTypeSupported?(t: string): boolean } }).MediaRecorder;
  return MIME_CANDIDATES.find((t) => MR?.isTypeSupported?.(t));
}

const SILENCE_MS = 1600;
const SPEECH_RMS = 0.04;

export interface RecorderOptions {
  maxSeconds?: number;
  /** koniec nagrania po ciszy (po pierwszych słowach) */
  stopOnSilence?: boolean;
  /** tekst przepisany przez serwer (wywoływane po każdym nagraniu) */
  onText(text: string, meta: { simulated: boolean }): void;
}

export interface RecorderState {
  status: RecorderStatus;
  /** sekundy nagrania (licznik na żywo) */
  elapsed: number;
  error: string | null;
  start(): Promise<void>;
  /** koniec nagrania i transkrypcja */
  stop(): void;
  /** koniec bez transkrypcji (zamknięcie panelu) */
  cancel(): void;
  reset(): void;
}

export function useAudioRecorder(opts: RecorderOptions): RecorderState {
  const maxSeconds = opts.maxSeconds ?? VOICE_TRANSCRIBE_MAX_SECONDS;
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const cancelledRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const release = useCallback(() => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    void audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    abortRef.current?.abort();
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    chunksRef.current = [];
    release();
    setStatus((s) => (s === 'recording' || s === 'transcribing' ? 'idle' : s));
  }, [release]);

  const watchSilence = useCallback(
    (stream: MediaStream) => {
      const Ctx =
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      try {
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        let heard = false;
        let quietSince = 0;
        const tick = () => {
          if (audioCtxRef.current !== ctx || recorderRef.current?.state !== 'recording') return;
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) sum += v * v;
          const rms = Math.sqrt(sum / buf.length);
          const now = performance.now();
          if (rms > SPEECH_RMS) {
            heard = true;
            quietSince = 0;
          } else if (heard) {
            quietSince ||= now;
            if (now - quietSince > SILENCE_MS) {
              stop();
              return;
            }
          }
          window.setTimeout(tick, 120);
        };
        tick();
      } catch {
        // bez Web Audio: koniec ręcznie albo po limicie
      }
    },
    [stop],
  );

  const start = useCallback(async () => {
    if (!recorderSupported()) {
      setStatus('error');
      setError(SPEECH_MESSAGES.unsupported);
      return;
    }
    setError(null);
    cancelledRef.current = false;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setStatus('denied');
        setError(SPEECH_MESSAGES.denied);
      } else {
        setStatus('error');
        setError(name === 'NotFoundError' ? SPEECH_MESSAGES.audioCapture : SPEECH_MESSAGES.generic);
      }
      return;
    }
    if (cancelledRef.current) {
      for (const t of stream.getTracks()) t.stop();
      return;
    }
    streamRef.current = stream;
    const mimeType = pickMime();
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      release();
      setStatus('error');
      setError(SPEECH_MESSAGES.generic);
      return;
    }
    recorderRef.current = rec;
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      const durationMs = Math.round(performance.now() - startedAtRef.current);
      const chunks = chunksRef.current;
      chunksRef.current = [];
      release();
      recorderRef.current = null;
      if (cancelledRef.current || chunks.length === 0) {
        setStatus('idle');
        return;
      }
      const blob = new Blob(chunks, { type: rec.mimeType || mimeType || 'audio/webm' });
      chunks.length = 0;
      setStatus('transcribing');
      const controller = new AbortController();
      abortRef.current = controller;
      transcribeAudio(blob, durationMs, controller.signal)
        .then((res) => {
          if (cancelledRef.current) return;
          setStatus('idle');
          if (!res.text.trim()) {
            setError(SPEECH_MESSAGES.noSpeech);
            return;
          }
          optsRef.current.onText(res.text.trim(), { simulated: res.simulated });
        })
        .catch((err) => {
          if (cancelledRef.current || (err instanceof DOMException && err.name === 'AbortError')) return;
          setStatus('error');
          setError(errorMessage(err, SPEECH_MESSAGES.generic));
        })
        .finally(() => {
          if (abortRef.current === controller) abortRef.current = null;
        });
    };
    startedAtRef.current = performance.now();
    setElapsed(0);
    setStatus('recording');
    rec.start(1000);
    timerRef.current = window.setInterval(() => {
      const sec = Math.floor((performance.now() - startedAtRef.current) / 1000);
      setElapsed(sec);
      if (sec >= maxSeconds) stop();
    }, 250);
    if (optsRef.current.stopOnSilence) watchSilence(stream);
  }, [maxSeconds, release, stop, watchSilence]);

  const reset = useCallback(() => {
    setError(null);
    setElapsed(0);
    setStatus((s) => (s === 'recording' || s === 'transcribing' ? s : 'idle'));
  }, []);

  // zamknięcie widoku = koniec nagrania bez wysyłania (mikrofon nie zostaje włączony w tle)
  useEffect(
    () => () => {
      cancelledRef.current = true;
      abortRef.current?.abort();
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') rec.stop();
      chunksRef.current = [];
      release();
    },
    [release],
  );

  return { status, elapsed, error, start, stop, cancel, reset };
}
